// frontend/src/components/dashboard/HeroResultado.jsx
import React from 'react';
import { TrendingUp, TrendingDown, Minus, ShieldCheck } from 'lucide-react';

/* =======================
   PALETA 02 — AURORA
======================= */
const AURORA = {
  emerald: '#34D399',
  purple: '#A78BFA',
  amber: '#F59E0B',
  border: 'rgba(255,255,255,0.10)',
  text: 'rgba(255,255,255,0.92)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
};

const formatMoney = (val) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(val || 0));

function getTrendUI(direction, percent) {
  if (direction === 'up') {
    return {
      Icon: TrendingUp,
      label: `+${percent}%`,
      pillStyle: {
        border: '1px solid rgba(52,211,153,0.25)',
        background: 'rgba(52,211,153,0.10)',
        color: 'rgba(167,243,208,0.95)',
      },
      text: `+${percent}% vs mês anterior`,
    };
  }
  if (direction === 'down') {
    return {
      Icon: TrendingDown,
      label: `−${percent}%`,
      pillStyle: {
        border: '1px solid rgba(245,158,11,0.25)',
        background: 'rgba(245,158,11,0.12)',
        color: 'rgba(253,230,138,0.95)',
      },
      text: `−${percent}% vs mês anterior`,
    };
  }
  if (direction === 'flat') {
    return {
      Icon: Minus,
      label: `0%`,
      pillStyle: {
        border: `1px solid ${AURORA.border}`,
        background: 'rgba(255,255,255,0.05)',
        color: AURORA.text2,
      },
      text: `0% vs mês anterior`,
    };
  }
  return null;
}

function AccentLine({ variant = 'mix' }) {
  const bg =
    variant === 'emerald'
      ? `linear-gradient(90deg, ${AURORA.emerald}, rgba(52,211,153,0.15))`
      : variant === 'purple'
        ? `linear-gradient(90deg, ${AURORA.purple}, rgba(167,139,250,0.15))`
        : `linear-gradient(90deg, ${AURORA.emerald}, ${AURORA.purple})`;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
      style={{ background: bg }}
    />
  );
}

function MetricCard({ title, value, accent = 'emerald', compact = false }) {
  const pad = compact ? 'p-4' : 'p-5';

  const iconBg =
    accent === 'emerald'
      ? 'linear-gradient(135deg, rgba(52,211,153,0.18), rgba(52,211,153,0.06))'
      : accent === 'purple'
        ? 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(167,139,250,0.06))'
        : 'linear-gradient(135deg, rgba(52,211,153,0.14), rgba(167,139,250,0.12))';

  const titleColor =
    accent === 'emerald' ? 'rgba(167,243,208,0.82)' : AURORA.text2;

  // ✅ AUMENTO DO GRID / ALTURA PADRÃO (pra evitar “gap” externo grande)
  const minH = compact ? 'min-h-[112px]' : 'min-h-[132px]';

  return (
    <div
      className={`relative h-full ${minH} overflow-hidden rounded-3xl border backdrop-blur ${pad}`}
      style={{
        borderColor: AURORA.border,
        background: 'rgba(255,255,255,0.05)',
      }}
    >
      <AccentLine variant={accent === 'purple' ? 'purple' : 'emerald'} />

      {/* halos sutis */}
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full blur-3xl"
        style={{
          background:
            accent === 'purple'
              ? 'rgba(167,139,250,0.18)'
              : 'rgba(52,211,153,0.14)',
        }}
      />
      <div
        className="pointer-events-none absolute -left-10 -bottom-10 h-44 w-44 rounded-full blur-3xl"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: titleColor }}
          >
            {title}
          </div>
          <div
            className={`${compact ? 'text-3xl' : 'text-[34px]'} mt-1 font-extrabold tracking-tight`}
            style={{ color: AURORA.text }}
          >
            {value}
          </div>
        </div>

        <div
          className={`grid place-items-center rounded-2xl border ${compact ? 'h-10 w-10' : 'h-11 w-11'}`}
          style={{
            borderColor: AURORA.border,
            background: iconBg,
          }}
          aria-hidden
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{
              background: accent === 'purple' ? AURORA.purple : AURORA.emerald,
              boxShadow: `0 0 0 6px rgba(255,255,255,0.04)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function HeroResultado({
  loading,
  vgvTotal = 0,
  faturamentoTotal,
  honorariosTotal = 0,
  tendenciaVgv, // { direction: 'up' | 'down' | 'flat', percent }
  compact = false,
}) {
  const faturamentoFinal =
    typeof faturamentoTotal === 'number' ? faturamentoTotal : honorariosTotal;

  if (loading) {
    return (
      <div
        className={`animate-pulse rounded-3xl border backdrop-blur ${
          compact ? 'h-44' : 'h-52'
        } bg-zinc-950/80`}
        style={{ borderColor: AURORA.border }}
      />
    );
  }

  const direction = tendenciaVgv?.direction;
  const percent = Number(tendenciaVgv?.percent || 0);
  const trendUI = getTrendUI(direction, percent);

  const pad = compact ? 'p-6' : 'p-7';
  const title = compact ? 'text-base' : 'text-lg';
  const gridGap = compact ? 'gap-4' : 'gap-6';
  const mt = compact ? 'mt-5' : 'mt-6';

  // ✅ “Aumentar o grid do hero”: garante altura mínima consistente,
  // para você poder voltar o gap externo ao padrão da página.
  const minHeroH = compact ? 'min-h-[260px]' : 'min-h-[300px]';

  return (
    <section
      className={`relative ${minHeroH} overflow-hidden rounded-3xl border bg-zinc-950/80 shadow-2xl backdrop-blur ${pad}`}
      style={{ borderColor: AURORA.border }}
    >
      {/* halos aurora */}
      <div
        className="pointer-events-none absolute -right-14 -top-14 h-56 w-56 rounded-full blur-3xl"
        style={{ background: 'rgba(167,139,250,0.18)' }}
      />
      <div
        className="pointer-events-none absolute -left-14 -bottom-14 h-56 w-56 rounded-full blur-3xl"
        style={{ background: 'rgba(52,211,153,0.16)' }}
      />

      {/* linha superior mix */}
      <AccentLine variant="mix" />

      <div className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <span
              className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.26em]"
              style={{ color: 'rgba(167,243,208,0.85)' }}
            >
              <ShieldCheck size={compact ? 11 : 12} />
              Resultado verificado
            </span>

            <h2
              className={`${title} font-semibold tracking-tight`}
              style={{ color: AURORA.text }}
            >
              Resultado financeiro
            </h2>

            <div className="text-[11px]" style={{ color: AURORA.text2 }}>
              Leitura rápida do seu desempenho no período.
            </div>
          </div>

          {trendUI && (
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold"
              style={trendUI.pillStyle}
            >
              <trendUI.Icon size={compact ? 13 : 14} />
              {trendUI.label}
            </div>
          )}
        </div>

        {/* ✅ grid “mais alto” (cards esticados e com min-height) */}
        <div
          className={`${mt} grid grid-cols-1 ${gridGap} md:grid-cols-2 md:auto-rows-fr`}
        >
          <MetricCard
            title="VGV Total"
            value={formatMoney(vgvTotal)}
            accent="emerald"
            compact={compact}
          />
          <MetricCard
            title="Faturamento (12m)"
            value={formatMoney(faturamentoFinal)}
            accent="purple"
            compact={compact}
          />
        </div>

        {trendUI && (
          <div
            className="mt-3 flex items-center gap-2 text-[11px]"
            style={{ color: AURORA.text2 }}
          >
            <trendUI.Icon className="h-4 w-4" style={{ color: AURORA.text3 }} />
            <span className="font-semibold" style={{ color: AURORA.text }}>
              {trendUI.text}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
